import { create } from 'zustand';
import type {
  AskBlock,
  ChatMessage,
  ClientChatStatus,
  ComposerState,
  Preview,
  RunSummary,
  SseClientEvent,
} from '@/api/models';
import { isKnownClientEventKind } from '@/api/models';
import { reportLeak } from '@/lib/telemetry';
import type { StreamPhase } from '@/lib/sse';
import { applyEvent, emptyLedger, seedFromRunSummary, type LedgerState } from '../ledger/projection';

/**
 * Stream state. Zustand owns this; TanStack Query owns everything with a URL.
 *
 * The rule is not stylistic. A run can emit dozens of frames a second, and
 * pushing each one through `setQueryData` re-runs every structural-sharing
 * comparison in the cache and re-renders every observer of that key. Query keeps
 * the chat document, which changes on a fetch; this store keeps the firehose,
 * which changes on a socket.
 */

/** An event kind this build does not recognise. Rendered loudly, never silently. */
export interface UnknownEvent {
  id: string;
  kind: string;
  at: string | null;
}

interface StreamState {
  projectId: number | null;
  connection: StreamPhase;
  /** Seconds until the next reconnect attempt, for the reconnect notice. */
  retryInMs: number | null;
  reconnectAttempt: number;

  ledger: LedgerState;
  /** Turns delivered by the stream, keyed by seq so a replay cannot double them. */
  liveMessages: Record<number, ChatMessage>;
  /** In-flight assistant text, accumulated per message seq until the `message` frame lands. */
  deltas: Record<number, string>;

  status: ClientChatStatus | null;
  composer: ComposerState | null;
  projectFrozen: boolean | null;
  preview: Preview | null;
  pendingAsk: { ask: AskBlock; answerableByClient: boolean } | null;
  unknown: UnknownEvent[];
  /** Bumped whenever the server tells us to re-fetch (stream_reset, status change). */
  refetchToken: number;

  reset: (projectId: number) => void;
  seed: (input: { activeRun: RunSummary | null; preview: Preview | null; status: ClientChatStatus | null; composer: ComposerState | null; pendingAsk: AskBlock | null }) => void;
  ingest: (event: SseClientEvent, cursor: string | null) => void;
  setConnection: (phase: StreamPhase, detail?: { attempt: number; retryInMs?: number }) => void;
  clearDelta: (seq: number) => void;
  requestRefetch: () => void;
}

const initial = {
  projectId: null as number | null,
  connection: 'connecting' as StreamPhase,
  retryInMs: null as number | null,
  reconnectAttempt: 0,
  ledger: emptyLedger(),
  liveMessages: {} as Record<number, ChatMessage>,
  deltas: {} as Record<number, string>,
  status: null as ClientChatStatus | null,
  composer: null as ComposerState | null,
  projectFrozen: null as boolean | null,
  preview: null as Preview | null,
  pendingAsk: null as StreamState['pendingAsk'],
  unknown: [] as UnknownEvent[],
  refetchToken: 0,
};

export const useStreamStore = create<StreamState>((set, get) => ({
  ...initial,

  reset: (projectId) => set({ ...initial, projectId, refetchToken: get().refetchToken }),

  seed: ({ activeRun, preview, status, composer, pendingAsk }) =>
    set((s) => ({
      ledger: seedFromRunSummary(s.ledger, activeRun, preview),
      preview: s.preview ?? preview,
      status: s.status ?? status,
      composer: s.composer ?? composer,
      pendingAsk:
        s.pendingAsk ?? (pendingAsk && !pendingAsk.answered ? { ask: pendingAsk, answerableByClient: true } : null),
    })),

  setConnection: (phase, detail) =>
    set({
      connection: phase,
      retryInMs: detail?.retryInMs ?? null,
      reconnectAttempt: detail?.attempt ?? 0,
    }),

  clearDelta: (seq) =>
    set((s) => {
      if (!(seq in s.deltas)) return s;
      const deltas = { ...s.deltas };
      delete deltas[seq];
      return { deltas };
    }),

  requestRefetch: () => set((s) => ({ refetchToken: s.refetchToken + 1 })),

  ingest: (event, cursor) => {
    const kind = (event as { kind?: string }).kind;

    // The leak detector. The server's projection is an allowlist and new kinds
    // default to internal, so an unrecognised kind here is either contract
    // drift or a scrub bug. Either way it must be impossible to miss in review
    // rather than quietly dropped or optimistically drawn.
    if (!kind || !isKnownClientEventKind(kind)) {
      reportLeak({ kind: 'unknown_stream_event', token: String(kind ?? 'missing').slice(0, 48), context: { cursor } });
      set((s) => ({
        unknown: s.unknown
          .concat({ id: cursor ?? `${Date.now()}-${s.unknown.length}`, kind: String(kind ?? 'missing'), at: (event as { at?: string }).at ?? null })
          .slice(-25),
      }));
      return;
    }

    const { ledger, preview } = applyEvent(get().ledger, event);
    const patch: Partial<StreamState> = { ledger };
    if (preview !== undefined && preview !== null) patch.preview = preview;

    switch (event.kind) {
      case 'stream_open': {
        // `status` on the open frame is the projected value for our role.
        if (event.status) patch.status = event.status as ClientChatStatus;
        break;
      }

      case 'stream_reset': {
        // Everything local is suspect: the server could not replay from our
        // cursor, so there is a hole we cannot see the size of. Drop the
        // stream-derived state and let the chat fetch rebuild it.
        set({
          ledger: emptyLedger(),
          liveMessages: {},
          deltas: {},
          unknown: [],
          pendingAsk: null,
          refetchToken: get().refetchToken + 1,
        });
        return;
      }

      case 'status': {
        patch.status = (event.client_visible_status ?? event.status) as ClientChatStatus;
        if (event.composer) patch.composer = event.composer;
        if (event.project_frozen !== undefined && event.project_frozen !== null) {
          patch.projectFrozen = event.project_frozen;
        }
        // A status change moves things the stream does not carry - done
        // availability, attachment budget, the preview's staleness. Re-fetch.
        patch.refetchToken = get().refetchToken + 1;
        break;
      }

      case 'text_delta': {
        const seq = event.message_seq;
        const previous = get().deltas[seq] ?? '';
        patch.deltas = { ...get().deltas, [seq]: previous + event.text };
        break;
      }

      case 'message': {
        const message = event.message;
        if (message) {
          patch.liveMessages = { ...get().liveMessages, [message.seq]: message };
          // The canonical turn replaces whatever the deltas built.
          const deltas = { ...get().deltas };
          delete deltas[message.seq];
          patch.deltas = deltas;
        }
        break;
      }

      case 'ask': {
        const ask = event.ask;
        if (ask && !ask.answered) {
          patch.pendingAsk = { ask, answerableByClient: (event.answerable_by ?? 'client') === 'client' };
        }
        break;
      }

      case 'ask_resolved': {
        const current = get().pendingAsk;
        if (current && current.ask.ask_id === event.ask_id) patch.pendingAsk = null;
        break;
      }

      case 'run_finished':
      case 'run_failed': {
        patch.pendingAsk = null;
        // The run is over; done availability and the composer state both moved.
        patch.refetchToken = get().refetchToken + 1;
        break;
      }

      default:
        break;
    }

    set(patch as StreamState);
  },
}));

/** Merge the fetched history with anything the stream delivered since. */
export function mergeMessages(fetched: ChatMessage[], live: Record<number, ChatMessage>): ChatMessage[] {
  const bySeq = new Map<number, ChatMessage>();
  for (const m of fetched) bySeq.set(m.seq, m);
  // The stream is fresher than a fetch that may have started before it.
  for (const m of Object.values(live)) bySeq.set(m.seq, m);
  return Array.from(bySeq.values()).sort((a, b) => a.seq - b.seq);
}
